import React from "react";
import { Link } from "react-router-dom";

// Inline parser: supports **bold** and [label](/route or https://url).
// Legal copy is plain text in the content modules; this renders emphasis and
// cross-references without exposing any markup to the reader.
function renderInline(text, keyBase) {
  if (text == null) return null;
  const nodes = [];
  // Split on links first, then bold within each segment.
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/g;
  let last = 0;
  let m;
  let i = 0;
  const pushText = (chunk) => {
    const parts = chunk.split("**");
    parts.forEach((p, idx) => {
      if (p === "") return;
      if (idx % 2 === 1) nodes.push(<b key={`${keyBase}-b-${i++}`}>{p}</b>);
      else nodes.push(<React.Fragment key={`${keyBase}-t-${i++}`}>{p}</React.Fragment>);
    });
  };
  while ((m = linkRe.exec(text)) !== null) {
    if (m.index > last) pushText(text.slice(last, m.index));
    const [, label, href] = m;
    const external = /^https?:\/\//.test(href);
    if (external) {
      nodes.push(
        <a key={`${keyBase}-l-${i++}`} href={href} target="_blank" rel="noopener noreferrer"
           className="font-medium text-pnr-green underline underline-offset-2 transition-colors hover:text-white focus-ring">{label}</a>
      );
    } else {
      nodes.push(
        <Link key={`${keyBase}-l-${i++}`} to={href}
           className="font-medium text-pnr-green underline underline-offset-2 transition-colors hover:text-white focus-ring">{label}</Link>
      );
    }
    last = m.index + m[0].length;
  }
  if (last < text.length) pushText(text.slice(last));
  return nodes;
}

function Block({ block, k }) {
  switch (block.type) {
    case "p":
      return <p>{renderInline(block.text, k)}</p>;
    case "ul":
      return (
        <ul className="ml-5 list-disc space-y-1">
          {block.items.map((it, idx) => <li key={`${k}-li-${idx}`}>{renderInline(it, `${k}-li-${idx}`)}</li>)}
        </ul>
      );
    case "callout":
      return (
        <div className="my-2 flex gap-3 rounded-2xl border border-surface-border bg-surface-3 p-4" data-testid="policy-npn">
          <p className="text-xs font-semibold leading-relaxed text-pnr-green">{renderInline(block.text, k)}</p>
        </div>
      );
    case "note":
      return (
        <div className="rounded-2xl border border-surface-border bg-surface-3 p-4 text-sm text-meta">
          {renderInline(block.text, k)}
        </div>
      );
    case "consent":
      return (
        <blockquote className="rounded-r-lg border-l-2 border-pnr-green/60 bg-surface-3 px-4 py-3 text-sm italic text-meta-strong">
          {renderInline(block.text, k)}
        </blockquote>
      );
    default:
      return null;
  }
}

function Section({ section }) {
  return (
    <section id={section.id ? `s-${section.id}` : undefined} className="mb-8" data-testid={`policy-section-${section.id || ""}`}>
      {section.title && (
        <h2 className="mb-3 font-display text-xl font-bold tracking-tight text-white">
          {section.id ? `${section.id}. ` : ""}{section.title}
        </h2>
      )}
      <div className="space-y-3 text-sm leading-relaxed text-meta">
        {section.blocks.map((b, idx) => <Block key={`${section.id}-${idx}`} k={`${section.id}-${idx}`} block={b} />)}
      </div>
    </section>
  );
}

// Renders a policy content object (intro blocks + numbered sections).
export default function PolicyView({ policy }) {
  return (
    <>
      {policy.intro && policy.intro.length > 0 && (
        <div className="mb-8 space-y-3 text-sm leading-relaxed text-meta" data-testid="policy-intro">
          {policy.intro.map((b, idx) => <Block key={`intro-${idx}`} k={`intro-${idx}`} block={b} />)}
        </div>
      )}
      {policy.sections.map((s) => <Section key={s.id || s.title} section={s} />)}
    </>
  );
}
